import { addDays, daysBetween, parseDay } from "./date";
import { dayMark } from "./dayMarks";
import { basedOn, windowWord, type Insight, type Window } from "./insight";
import { MEALS, entriesFor, foodOn, proteinTarget } from "./intake";
import { SAT_FAT_ENERGY_MAX, SODIUM_MAX_MG, dailyTargets } from "./nutrition";
import { choice, symptomsOf } from "./symptoms";
import type { FoodEntry, KayaState, MealSlot } from "./types";

/**
 * Food over a window, for Progress › Food (ADR-033, research §2.5).
 *
 * A day counts only when something was logged on it. There is no per-day "done" flag, so a
 * logged day is a day with at least one live entry; an unlogged day is "not known", never a day
 * of eating nothing — treating it as zero would invent a deficit on every forgotten day.
 *
 * A nutrient an entry does not carry is "not known" (invariant 18), so a day's total for it is
 * only known when every entry that day carries it. A partial sum would read low and look like
 * advice; such days are left out of that nutrient's mean and `known` says how many were left.
 */

export const MIN_LOGGED_DAYS = 7;
export const MIN_EACH_SIDE = 4;
export const MIN_PHASE_DAYS = 7;
/** Research §2.5 F4: 25–30 g a meal is where muscle protein synthesis tops out. */
export const MEAL_PROTEIN_AIM_G = 25;

/** The window's dates, oldest first: `today − (w − 1)` … `today`, or from the start date for
 *  window 0. Days before the plan began are not counted — they could never have been logged. */
export function windowDates(S: KayaState, today: string, w: Window): string[] {
  const start = S.profile.startDate;
  let from = w === 0 ? start || today : addDays(today, -(w - 1));
  if (start && /^\d{4}-\d{2}-\d{2}$/.test(start) && start > from) from = start;
  const n = daysBetween(from, today) + 1;
  if (!(n > 0)) return [];
  return Array.from({ length: n }, (_, i) => addDays(from, i));
}

export function isWeekend(ds: string): boolean {
  const d = parseDay(ds).getDay();
  return d === 0 || d === 6;
}

/** A heavy flow logged on any day of the window — `flow:heavy` or the legacy `heavy` key. The
 *  iron sentence's blood-test clause needs this, not just a female profile with cycle tracking
 *  on: a low iron reading on its own is not evidence of a heavy flow. */
function heavyFlowInWindow(S: KayaState, today: string, w: Window): boolean {
  return windowDates(S, today, w).some((ds) => choice(symptomsOf(S, ds), "flow") === "heavy");
}

interface LoggedDay {
  ds: string;
  entries: FoodEntry[];
  kcal: number;
}

/** The complete days of the window (ADR-049): food logged, and the day not marked untracked or a
 *  fast. Every average on Progress › Food is over these, so a half-logged day marked as such can
 *  no longer drag one down. */
function loggedDays(S: KayaState, today: string, w: Window): LoggedDay[] {
  const out: LoggedDay[] = [];
  for (const ds of windowDates(S, today, w)) {
    if (dayMark(S, ds)) continue;
    const entries = entriesFor(S, ds);
    if (entries.length) out.push({ ds, entries, kcal: entries.reduce((a, e) => a + e.kcal, 0) });
  }
  return out;
}

const mean = (xs: number[]): number | null =>
  xs.length ? xs.reduce((a, x) => a + x, 0) / xs.length : null;

/* ---------- coverage and budget ---------- */

export interface Coverage {
  /** complete days */
  logged: number;
  /** the window's days less its fast days — a fast day is never a missed one (ADR-049) */
  of: number;
  /** 0–1; null for an empty window */
  share: number | null;
  /** days marked as a fast, left out of `of` */
  fast: number;
  /** days marked untracked: in `of`, not in `logged` */
  untracked: number;
}

export function coverage(S: KayaState, today: string, w: Window): Coverage {
  const dates = windowDates(S, today, w);
  const fast = dates.filter((ds) => dayMark(S, ds) === "fast").length;
  const untracked = dates.filter((ds) => dayMark(S, ds) === "untracked").length;
  const of = dates.length - fast;
  const logged = loggedDays(S, today, w).length;
  return { logged, of, share: of ? logged / of : null, fast, untracked };
}

export interface OverDay {
  ds: string;
  kcal: number;
  budget: number;
  weekend: boolean;
  prePeriod: boolean;
}

export interface Adherence {
  /** logged days In ≤ budget */
  within: number;
  /** logged days whose budget is known */
  of: number;
  over: OverDay[];
}

/** Logged days within the day's budget. A day with no budget (no body numbers) is not judged. */
export function energyAdherence(
  S: KayaState,
  today: string,
  w: Window,
  budget: (ds: string) => number | null,
  prePeriod: (ds: string) => boolean = () => false
): Adherence {
  let within = 0;
  let of = 0;
  const over: OverDay[] = [];
  for (const d of loggedDays(S, today, w)) {
    const b = budget(d.ds);
    if (b === null || !Number.isFinite(b)) continue;
    of++;
    if (d.kcal <= b) within++;
    else over.push({ ds: d.ds, kcal: d.kcal, budget: b, weekend: isWeekend(d.ds), prePeriod: prePeriod(d.ds) });
  }
  return { within, of, over };
}

/* ---------- nutrients ---------- */

export type NutrientKey = "kcal" | "p" | "c" | "f" | "fb" | "sf" | "na" | "ca" | "fe" | "pot";
export const NUTRIENT_KEYS: NutrientKey[] = ["kcal", "p", "c", "f", "fb", "sf", "na", "ca", "fe", "pot"];

const NUTRIENT_META: Record<NutrientKey, { label: string; unit: "kcal" | "g" | "mg"; limit: boolean }> = {
  kcal: { label: "In", unit: "kcal", limit: false },
  p: { label: "Protein", unit: "g", limit: false },
  c: { label: "Carbohydrate", unit: "g", limit: false },
  f: { label: "Fat", unit: "g", limit: false },
  fb: { label: "Fibre", unit: "g", limit: false },
  sf: { label: "Saturated fat", unit: "g", limit: true },
  na: { label: "Sodium", unit: "mg", limit: true },
  ca: { label: "Calcium", unit: "mg", limit: false },
  fe: { label: "Iron", unit: "mg", limit: false },
  pot: { label: "Potassium", unit: "mg", limit: false },
};

/** A day's total of one nutrient, or null when any entry that day does not carry it. */
export function dayValue(entries: FoodEntry[], k: NutrientKey): number | null {
  if (!entries.length) return null;
  let s = 0;
  for (const e of entries) {
    const v = e[k];
    if (typeof v !== "number" || !Number.isFinite(v)) return null;
    s += v;
  }
  return s;
}

export interface NutrientAverage {
  key: NutrientKey;
  label: string;
  unit: "kcal" | "g" | "mg";
  /** mean over the days it is known on; null when it is known on none */
  avg: number | null;
  /** logged days the whole day's value is known */
  known: number;
  /** a goal, or for a limit the ceiling; null when it needs body numbers Kaya lacks */
  target: number | null;
  /** sodium and saturated fat: a ceiling, where filling it is the warning */
  limit: boolean;
  /** only for a limit: over it on average; null when not known */
  over: boolean | null;
}

export interface NutrientAverages {
  logged: number;
  by: Record<NutrientKey, NutrientAverage>;
  /** saturated fat as a share of kcal, in percent, over the days sat fat is known */
  satFatPct: number | null;
  /** the ceiling for `satFatPct` */
  satFatMaxPct: number;
}

const round = (n: number, dp: number) => Math.round(n * 10 ** dp) / 10 ** dp;

export function nutrientAverages(S: KayaState, today: string, w: Window): NutrientAverages {
  const days = loggedDays(S, today, w);
  const T = dailyTargets(S, parseDay(today));
  const targets: Record<NutrientKey, number | null> = {
    kcal: T.kcal,
    p: T.protein,
    c: T.carbs,
    f: T.fat,
    fb: T.fibre,
    sf: T.satFatMax,
    na: T.sodiumMax,
    ca: T.calcium,
    fe: T.iron,
    pot: T.potassium,
  };

  let sfEnergy = 0;
  let sfKcal = 0;
  let sfDays = 0;
  for (const d of days) {
    const sf = dayValue(d.entries, "sf");
    if (sf === null || !(d.kcal > 0)) continue;
    sfEnergy += sf * 9;
    sfKcal += d.kcal;
    sfDays++;
  }
  const satFatPct = sfDays ? round((sfEnergy / sfKcal) * 100, 1) : null;

  const by = {} as Record<NutrientKey, NutrientAverage>;
  for (const key of NUTRIENT_KEYS) {
    const vals = days.map((d) => dayValue(d.entries, key)).filter((v): v is number => v !== null);
    const m = mean(vals);
    const meta = NUTRIENT_META[key];
    const avg = m === null ? null : round(m, meta.unit === "g" ? 1 : 0);
    let over: boolean | null = null;
    if (key === "na" && avg !== null) over = avg > SODIUM_MAX_MG;
    if (key === "sf") {
      if (satFatPct !== null) over = satFatPct > SAT_FAT_ENERGY_MAX * 100;
      else if (avg !== null && targets.sf !== null) over = avg > targets.sf;
    }
    by[key] = { key, label: meta.label, unit: meta.unit, avg, known: vals.length, target: targets[key], limit: meta.limit, over };
  }
  return { logged: days.length, by, satFatPct, satFatMaxPct: SAT_FAT_ENERGY_MAX * 100 };
}

/* ---------- protein ---------- */

export interface ProteinByMeal {
  /** mean grams per logged day; a logged day without the meal adds 0. Null for a slot never
   *  eaten in the window, which is not a meal she has at all. */
  bySlot: Record<MealSlot, number | null>;
  aim: number;
  /** eaten slots under the aim, in meal order */
  under: MealSlot[];
  /** the day's target less the day's mean, never below 0 */
  gap: number | null;
  target: number;
  avg: number | null;
  logged: number;
}

export function proteinByMeal(S: KayaState, today: string, w: Window): ProteinByMeal {
  const days = loggedDays(S, today, w);
  const target = proteinTarget(S);
  const bySlot = { breakfast: null, lunch: null, dinner: null, snack: null } as Record<MealSlot, number | null>;
  for (const slot of MEALS) {
    if (!days.some((d) => d.entries.some((e) => e.meal === slot))) continue;
    const perDay = days.map((d) => d.entries.filter((e) => e.meal === slot).reduce((a, e) => a + e.p, 0));
    bySlot[slot] = round(mean(perDay) ?? 0, 1);
  }
  const avg = mean(days.map((d) => d.entries.reduce((a, e) => a + e.p, 0)));
  const under = MEALS.filter((m) => {
    const v = bySlot[m];
    return v !== null && v < MEAL_PROTEIN_AIM_G;
  });
  return {
    bySlot,
    aim: MEAL_PROTEIN_AIM_G,
    under,
    gap: avg === null ? null : Math.max(0, Math.round(target - avg)),
    target,
    avg: avg === null ? null : round(avg, 1),
    logged: days.length,
  };
}

export interface ProteinDays {
  atTarget: number;
  logged: number;
  target: number;
}

export function proteinDays(S: KayaState, today: string, w: Window): ProteinDays {
  const days = loggedDays(S, today, w);
  const target = proteinTarget(S);
  const atTarget = days.filter((d) => d.entries.reduce((a, e) => a + e.p, 0) >= target).length;
  return { atTarget, logged: days.length, target };
}

/* ---------- patterns ---------- */

export interface WeekendDrift {
  weekend: number | null;
  weekday: number | null;
  weekendDays: number;
  weekdayDays: number;
  /** mean In on weekend days less weekdays; null under `MIN_EACH_SIDE` logged days of each */
  drift: number | null;
}

export function weekendDrift(S: KayaState, today: string, w: Window): WeekendDrift {
  const days = loggedDays(S, today, w);
  const we = days.filter((d) => isWeekend(d.ds)).map((d) => d.kcal);
  const wd = days.filter((d) => !isWeekend(d.ds)).map((d) => d.kcal);
  const a = mean(we);
  const b = mean(wd);
  return {
    weekend: a === null ? null : Math.round(a),
    weekday: b === null ? null : Math.round(b),
    weekendDays: we.length,
    weekdayDays: wd.length,
    drift: a !== null && b !== null && we.length >= MIN_EACH_SIDE && wd.length >= MIN_EACH_SIDE ? Math.round(a - b) : null,
  };
}

export interface PhaseIntake {
  pre: number | null;
  other: number | null;
  preDays: number;
  otherDays: number;
  /** pre-period mean In less the other days'; null under `MIN_PHASE_DAYS` logged days each */
  diff: number | null;
  /** logged pre-period days that also logged a sweet craving */
  sweetDays: number;
}

export function intakeByPhase(
  S: KayaState,
  today: string,
  w: Window,
  prePeriod: (ds: string) => boolean
): PhaseIntake {
  const days = loggedDays(S, today, w);
  const pre = days.filter((d) => prePeriod(d.ds));
  const other = days.filter((d) => !prePeriod(d.ds));
  const a = mean(pre.map((d) => d.kcal));
  const b = mean(other.map((d) => d.kcal));
  const sweetDays = pre.filter((d) => (S.logs[d.ds]?.symptoms || []).includes("cravings:sweet")).length;
  return {
    pre: a === null ? null : Math.round(a),
    other: b === null ? null : Math.round(b),
    preDays: pre.length,
    otherDays: other.length,
    diff: a !== null && b !== null && pre.length >= MIN_PHASE_DAYS && other.length >= MIN_PHASE_DAYS ? Math.round(a - b) : null,
    sweetDays,
  };
}

export interface Source {
  name: string;
  amount: number;
}

/** The three foods that carried most of a nutrient over the window. Entries that do not carry it
 *  contribute nothing rather than a guessed 0. The same food logged under one code is one source. */
export function topSources(S: KayaState, today: string, w: Window, nutrient: NutrientKey): Source[] {
  const sums = new Map<string, Source>();
  for (const d of loggedDays(S, today, w)) {
    for (const e of d.entries) {
      const v = e[nutrient];
      if (typeof v !== "number" || !(v > 0)) continue;
      const key = e.code || e.name.trim().toLowerCase();
      const cur = sums.get(key);
      if (cur) cur.amount += v;
      else sums.set(key, { name: e.name, amount: v });
    }
  }
  return [...sums.values()]
    .sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name))
    .slice(0, 3)
    .map((s) => ({ name: s.name, amount: round(s.amount, 1) }));
}

/* ---------- the sentences ---------- */

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
const NUM_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
const numWord = (n: number) => NUM_WORDS[n] ?? String(n);
const numWordLower = (n: number) => (n <= 10 ? numWord(n).toLowerCase() : String(n));
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);
const more = (n: number, what: string) => `after ${n} more ${n === 1 ? what : `${what}s`}`;

/** How a meal slot reads inside a sentence. */
const MEAL_WORD: Record<MealSlot, string> = {
  breakfast: "breakfast",
  lunch: "lunch",
  dinner: "dinner",
  snack: "the evening snack",
};

function andList(xs: string[]): string {
  if (xs.length <= 1) return xs.join("");
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

/** Foods that lift each nutrient — Indian staples, named so the sentence can be acted on. */
const LIFTS: Partial<Record<NutrientKey, string>> = {
  fe: "Dal, spinach, ragi and jaggery lift it",
  fb: "Dal, chana, fruit and whole grains lift it",
  ca: "Curd, paneer, ragi and milk lift it",
  pot: "Dal, bananas, potatoes and leafy greens lift it",
};

export interface FoodDeps {
  budget: (ds: string) => number | null;
  prePeriod: (ds: string) => boolean;
  cycleOn: boolean;
}

const CYCLE_LINK = { to: "/progress/cycle", label: "See your cycle" };

/** The weakest goal nutrient and the limit that is over (F5–F9, merged as on the artboard), with
 *  what the sentence is about so the group above it can say so in its heading. */
function weakest(S: KayaState, today: string, w: Window, deps: FoodDeps): { insight: Insight; about: string[] } {
  const loggedBasis = (n: number) => `${n} logged ${plural(n, "day")}`;
  const na = nutrientAverages(S, today, w);
  const candidates = (["fb", "fe", "ca", "pot"] as NutrientKey[])
    .map((k) => na.by[k])
    .filter((r) => r.known >= MIN_LOGGED_DAYS && r.avg !== null && r.target !== null && r.target > 0);
  if (!candidates.length) {
    const most = Math.max(0, ...(["fb", "fe", "ca", "pot"] as NutrientKey[]).map((k) => na.by[k].known));
    return {
      about: [],
      insight: {
        key: "food.weakest",
        text: "Nutrients against their targets appear after 7 logged days with minerals on record.",
        basedOn: basedOn(loggedBasis(most)),
        wait: more(Math.max(1, MIN_LOGGED_DAYS - most), "logged day"),
      },
    };
  }
  const about: string[] = [];
  const ratio = (r: NutrientAverage) => (r.avg ?? 0) / (r.target ?? 1);
  const low = [...candidates].sort((a, b) => ratio(a) - ratio(b))[0];
  let text: string;
  if (ratio(low) >= 1) {
    text = "Fibre and the minerals Kaya tracks reach their targets on average.";
    about.push("Fibre and minerals");
  } else {
    text = `${low.label} is the weakest nutrient at ${fmt(low.avg ?? 0)} of ${fmt(low.target ?? 0)} ${low.unit}.`;
    about.push(low.label);
    const lift = LIFTS[low.key];
    if (lift) {
      const blood =
        low.key === "fe" && S.profile.sex === "female" && deps.cycleOn && heavyFlowInWindow(S, today, w);
      text += blood ? ` ${lift}; with heavy flow and tiredness together it is worth a blood test.` : ` ${lift}.`;
    }
  }
  const sf = na.by.sf;
  const sodium = na.by.na;
  if (sf.over && sf.known >= MIN_LOGGED_DAYS) {
    const src = topSources(S, today, w, "sf").slice(0, 2).map((s, i) => (i ? lowerFirst(s.name) : s.name));
    if (src.length === 2) text += ` ${src[0]} and ${src[1]} are the two biggest sources of saturated fat.`;
    else if (src.length === 1) text += ` ${src[0]} is the biggest source of saturated fat.`;
    if (src.length) about.push("saturated fat");
  } else if (sodium.over && sodium.known >= MIN_LOGGED_DAYS && sodium.avg !== null) {
    text += ` Sodium runs ${fmt(sodium.avg - SODIUM_MAX_MG)} mg a day over the ${fmt(SODIUM_MAX_MG)} mg limit.`;
    about.push("sodium");
  }
  return { about, insight: { key: "food.weakest", text, basedOn: basedOn(loggedBasis(low.known)) } };
}

/** The weakest-nutrient group's heading: what its sentence is about ("Iron and saturated fat"). */
export function weakestHeading(S: KayaState, today: string, w: Window, deps: FoodDeps): string {
  const { about } = weakest(S, today, w, deps);
  return about.length ? cap(andList(about)) : "Nutrients";
}

/** Protein over the window in one sentence — the figure the chapter's hero carried before the
 *  cover took its place. Null with nothing logged. */
export function proteinDaysInsight(S: KayaState, today: string, w: Window): Insight | null {
  const pd = proteinDays(S, today, w);
  const pm = proteinByMeal(S, today, w);
  if (!pd.logged || pm.avg === null) return null;
  return {
    key: "food.proteinDays",
    text: `Protein averaged ${fmt(pm.avg)} g a day of ${fmt(pd.target)} g and reached the target on ${pd.atTarget} of ${pd.logged} logged ${plural(pd.logged, "day")}.`,
    basedOn: basedOn(`${pd.logged} logged ${plural(pd.logged, "day")}`),
  };
}

export function foodInsights(S: KayaState, today: string, w: Window, deps: FoodDeps): Insight[] {
  if (!foodOn(S)) return [];
  const out: Insight[] = [];
  const cov = coverage(S, today, w);
  const win = windowWord(w);
  const pre = (ds: string) => deps.cycleOn && deps.prePeriod(ds);
  const loggedBasis = (n: number) => `${n} logged ${plural(n, "day")}`;
  // "since you started" is a window, not a count — alone it reads "based on since you started".
  const inWindow = (n: number) => (w === 0 ? `${loggedBasis(n)} since you started` : basedOn(loggedBasis(n), win));
  const short = cov.logged < MIN_LOGGED_DAYS;
  const waitDays = more(MIN_LOGGED_DAYS - cov.logged, "logged day");

  // F1 — always shown: every other food figure inherits it.
  const unlogged = cov.of - cov.logged;
  // Marked days say why they are out (ADR-049): an untracked day is among the unlogged ones, and a
  // fast day is not a missed day at all, so it is out of the count and named on its own.
  const marked = cov.untracked ? ` (${cov.untracked} marked untracked)` : "";
  const fasts = cov.fast ? ` ${cov.fast === 1 ? "The fast day is" : `The ${cov.fast} fast days are`} left out too, never missed.` : "";
  out.push({
    key: "food.coverage",
    text:
      cov.logged === 0
        ? `No food logged ${w === 0 ? win : `in ${win}`}.${fasts}`
        : unlogged === 0
          ? `All ${cov.of} days logged. Every food figure on this page is from those days.${fasts}`
          : `${cov.logged} of ${cov.of} days logged. Every food figure on this page is from those days; the ${unlogged} unlogged ${plural(unlogged, "day", "days")}${marked} ${plural(unlogged, "is", "are")} not counted as zero.${fasts}`,
    basedOn: w === 0 ? inWindow(cov.logged) : basedOn(win),
  });

  // F2
  const adh = energyAdherence(S, today, w, deps.budget, pre);
  if (short) {
    out.push({ key: "food.adherence", text: "In against budget appears after 7 logged days.", basedOn: basedOn(loggedBasis(cov.logged)), wait: waitDays });
  } else if (adh.of === 0) {
    out.push({
      key: "food.adherence",
      text: "In against budget needs your year of birth and sex, which set the budget.",
      basedOn: basedOn(loggedBasis(cov.logged)),
      wait: "after your body numbers are in",
    });
  } else {
    const n = adh.over.length;
    let text = n === 0 ? `In was within budget on all ${adh.of} logged days.` : `In was within budget on ${adh.within} of ${adh.of} logged days`;
    let cycleTagged = false;
    if (n > 0) {
      const weekends = adh.over.filter((d) => d.weekend).length;
      const pres = adh.over.filter((d) => d.prePeriod).length;
      const covered = adh.over.every((d) => d.weekend || d.prePeriod);
      const where =
        !covered ? null
        : pres === 0 ? (n === 1 ? "a weekend day" : "weekends")
        : weekends === 0 || adh.over.every((d) => d.prePeriod) ? "in the pre-period week"
        : "weekends or the pre-period week";
      if (where) {
        text += n === 1 ? `; the 1 over-day was ${where}.` : `; the ${n} over-days were all ${where}.`;
        cycleTagged = pres > 0;
      } else text += ".";
    }
    out.push({
      key: "food.adherence",
      text,
      basedOn: basedOn(loggedBasis(adh.of)),
      ...(cycleTagged ? { tag: "cycle" as const, link: CYCLE_LINK } : {}),
    });
  }

  // F3/F4
  const pm = proteinByMeal(S, today, w);
  if (short) {
    out.push({ key: "food.protein", text: "Protein by meal appears after 7 logged days.", basedOn: basedOn(loggedBasis(cov.logged)), wait: waitDays });
  } else {
    const eaten = MEALS.filter((m) => pm.bySlot[m] !== null);
    const reach = eaten.length - pm.under.length;
    // "None of the four meals reaches", "One of four meals reaches", "Two of four meals reach".
    let text =
      reach === 0
        ? `None of the ${numWordLower(eaten.length)} meals reaches ${pm.aim} g.`
        : `${numWord(reach)} of ${numWordLower(eaten.length)} meals ${reach === 1 ? "reaches" : "reach"} ${pm.aim} g.`;
    if (pm.under.length && pm.gap) {
      // Bringing each short meal to the aim either covers the day's gap or only narrows it.
      const room = pm.under.reduce((a, m) => a + (pm.aim - (pm.bySlot[m] ?? 0)), 0);
      const names = cap(andList(pm.under.map((m) => MEAL_WORD[m])));
      const verb = room >= pm.gap ? "closes" : "narrows";
      text +=
        pm.under.length === 1
          ? ` ${names} is where the ${pm.gap} g gap sits — an egg or a bowl of curd there ${verb} it.`
          : ` ${names} are where the ${pm.gap} g gap sits — an egg or a bowl of curd at each ${verb} it.`;
    } else if (!pm.gap) {
      text += ` Protein averages ${fmt(pm.avg ?? 0)} g, at the ${pm.target} g target.`;
    }
    out.push({ key: "food.protein", text, basedOn: basedOn(loggedBasis(pm.logged)) });
  }

  // F5–F9, merged as on the artboard: the weakest nutrient, then the limit that is over.
  out.push(weakest(S, today, w, deps).insight);

  // F10
  const wk = weekendDrift(S, today, w);
  if (wk.drift === null) {
    const need = Math.max(MIN_EACH_SIDE - wk.weekendDays, MIN_EACH_SIDE - wk.weekdayDays, 1);
    const side = wk.weekendDays < MIN_EACH_SIDE ? "logged weekend day" : "logged weekday";
    out.push({
      key: "food.weekend",
      text: "Weekends against weekdays appear after 4 logged days of each.",
      basedOn: inWindow(cov.logged),
      wait: more(need, side),
    });
  } else {
    const d = Math.round(wk.drift / 10) * 10;
    let text: string;
    if (Math.abs(d) < 100) text = "Weekends and weekdays run within 100 kcal of each other.";
    else if (d > 0) {
      text = `Weekends run +${fmt(d)} kcal over weekdays`;
      const n = adh.over.length;
      const we = adh.over.filter((x) => x.weekend).length;
      text += n > 0 && we === n ? " — that is all of the over-budget days." : n > 0 && we * 2 > n ? " — that is most of the over-budget days." : ".";
    } else text = `Weekends run ${fmt(-d)} kcal under weekdays.`;
    out.push({ key: "food.weekend", text, basedOn: inWindow(cov.logged) });
  }

  // F12 — over the whole record: a single month holds one pre-period week at most.
  if (deps.cycleOn) {
    const ph = intakeByPhase(S, today, 0, deps.prePeriod);
    const periods = (S.cycle?.starts || []).filter((s) => s <= today).length;
    const periodsBasis = `${periods} logged ${plural(periods, "period")}`;
    if (ph.diff === null) {
      const need = Math.max(MIN_PHASE_DAYS - ph.preDays, MIN_PHASE_DAYS - ph.otherDays, 1);
      out.push({
        key: "food.cycle",
        text: "Appetite before a period appears after 7 logged days in the pre-period week.",
        basedOn: basedOn(periodsBasis),
        tag: "cycle",
        wait: more(need, ph.preDays < MIN_PHASE_DAYS ? "logged pre-period day" : "logged day"),
        link: CYCLE_LINK,
      });
    } else {
      const d = Math.round(ph.diff / 10) * 10;
      const sweet = ph.sweetDays * 2 > ph.preDays ? ", mostly sweet" : "";
      const text =
        Math.abs(d) < 50
          ? "In the week before your period you eat about the same as the rest of the month."
          : d > 0
            ? `In the week before your period you eat ~${fmt(d)} kcal more a day${sweet}. That is normal; the budget can carry it.`
            : `In the week before your period you eat ~${fmt(-d)} kcal less a day.`;
      out.push({ key: "food.cycle", text, basedOn: basedOn(periodsBasis), tag: "cycle", link: CYCLE_LINK });
    }
  }

  return out;
}
